import type { CharacterShape } from "../handwriting/stroke-shape.js";
import type { GradedAnswer } from "./answer-marks.js";
import type {
  HandwritingIndex,
  HandwritingEntry,
} from "../handwriting/handwriting-lookup.js";
import type { HandwritingReading } from "./handwriting-verdict.js";
import { type Rating, ratings } from "./rating.js";
import {
  inOrderCost,
  orderFreeCost,
} from "../handwriting/stroke-assignment.js";
import { lookUpHandwriting } from "../handwriting/handwriting-lookup.js";
import { strokeFindings } from "../handwriting/stroke-attempt.js";

/**
 * Marking a character a reader drew against the one the card asked for.
 *
 * The ink goes through the same recogniser the 词典 pad uses, and what the
 * card asks of the answer is where it came in the ranking. Nothing is shown
 * while the reader writes and nothing snaps into place, so the character on
 * the square is theirs, and what is marked is whether it was the one they were
 * asked for.
 *
 * **First or nothing, and the measurements are why.** Over a 300 character
 * sample of the index, ink taken from a character's own outlines and displaced
 * as a hand displaces it comes first 99.5% of the time. Ink taken from the
 * character it most resembles comes first 0.0% of the time, and puts the
 * asked-for character *second* in 48% of them. Among the confusions a learner
 * actually makes it is nine of twenty: a reader who writes 末 for 未, 日 for 曰,
 * 王 for 玉, 很 for 狠 or 场 for 扬 leaves the asked-for character at rank 2. A
 * window taking second place would hand the middle rating to the pairs this
 * card exists to separate. `docs/fuxi.md` has the tables.
 *
 * **The middle is stroke order, which is a real degree of knowing.**
 * `grade-hanzi.ts` states the rule: a middle rating has to be something a
 * reader half knew, not a threshold somebody invented. Typing the other
 * writing of a word earns it there and right sounds under the wrong tone earns
 * it in pinyin. Here it is the right character written in an order it is not
 * taught in, which {@link strokeOrderGap} separates cleanly.
 *
 * **The stroke count has to match, and that is the whole of what pressing
 * Check means.** The recogniser scores a half-written character on purpose, so
 * that three strokes of 海 bring back 氵 in a search box. A reader who stops
 * eight strokes into 然 would otherwise be ranked against characters they had
 * not finished. Nobody tells them when they are done, so finishing is part of
 * the answer.
 *
 * **The reading also says what became of each stroke**, wherever the reader
 * was not marked right and was writing the character asked for. The marked
 * card colours the strokes from it. Nothing about the rating is read from it.
 * See `src/handwriting/stroke-comparison.ts`.
 *
 * **`easy` is never reached**, for the reason `answer-marks.ts` gives. Nothing
 * in a drawn character says a reader found it easy, and drawing is slow enough
 * that the time it took says less here than anywhere else.
 */

/**
 * How much dearer the taught order may be than the best pairing.
 *
 * Measured over the same 300 characters. Ink that keeps the taught order never
 * exceeded 0.020, at any percentile and in the worst single case. Ink with two
 * adjacent strokes swapped sat at 0.129 at the median. At 0.03 the reading
 * catches 90.1% of swapped strokes and misreads none of the writing that was
 * in order, which is the way round it has to fail: telling a reader who wrote
 * the character properly that they wrote it in the wrong order is worse than
 * letting a marginal swap pass.
 */
export const strokeOrderGap = 0.03;

/** What marking one drawn character needs. */
export interface HandwritingAnswer {
  /** The character the card asked for. */
  readonly expected: string;

  /** The ink, as the page described it. */
  readonly shape: CharacterShape;

  /** The characters to rank it against. */
  readonly index: HandwritingIndex;
}

/** A marked handwriting answer, and what the page can say about it. */
export interface GradedHandwriting extends GradedAnswer {
  /** What the site made of the ink. */
  readonly reading: HandwritingReading;
}

/**
 * Mark a drawn character against the one the card asked for.
 *
 * @param answer - The character wanted, the ink, and what to rank it against.
 * @returns The rating, the mark, and what the site made of the ink.
 */
export function gradeHandwriting(answer: HandwritingAnswer): GradedHandwriting {
  const entry = answer.index.find((one) => one.character === answer.expected);
  const reading = readInk({ ...answer, entry });

  return {
    rating: ratingOf(reading.verdict),
    pieces: [markOf(answer.expected, reading)],
    accepted: [answer.expected],
    reading,
  };
}

/** What the site made of the ink. */
function readInk(properties: {
  expected: string;
  shape: CharacterShape;
  index: HandwritingIndex;
  entry: HandwritingEntry | undefined;
}): HandwritingReading {
  const { expected, shape, index, entry } = properties;
  const counts = { drawn: shape.length, expected: entry?.shape.length ?? 0 };

  if (entry === undefined) {
    // A card is never introduced for a character the index has no strokes for,
    // so this is an index that has moved under a card rather than a reader who
    // did anything. `src/user/handwriting-subjects.ts` is what keeps them out.
    return { verdict: "unrecognised", ...counts };
  }

  if (shape.length === 0) {
    return { verdict: "blank", ...counts };
  }

  const findings = strokeFindings({ ...properties, stored: entry.shape });

  if (shape.length !== entry.shape.length) {
    return { verdict: "wrongCount", ...counts, ...findings.shown };
  }

  const gap =
    inOrderCost(shape, entry.shape) - orderFreeCost(shape, entry.shape);
  const ranked = lookUpHandwriting({ index, shape, limit: 1 });
  const first = ranked[0]?.character;
  const reordered: HandwritingReading = {
    verdict: "outOfOrder",
    ...counts,
    strokes: findings.compared,
  };

  if (first === expected) {
    return gap > strokeOrderGap ? reordered : { verdict: "written", ...counts };
  }

  return gap > strokeOrderGap && isFirstOutOfOrder(properties)
    ? reordered
    : {
        verdict: "another",
        ...counts,
        ...findings.shown,
        ...(first === undefined ? {} : { looksLike: first }),
      };
}

/**
 * Whether the answer is the nearest character once stroke order is left out.
 *
 * Asked only of an answer that did not come first as it was written, which is
 * the 7.6% of swapped strokes the ordered ranking loses, and the reader who
 * writes a whole component in an order of their own. It ranks a second time
 * with the strokes matched freely rather than in the order they were drawn,
 * over the characters of that stroke count alone. That field is what the
 * reader has already been held to, and it keeps the second pass to a fraction
 * of the index.
 *
 * **A different character cannot reach it.** Over the same sample, ink taken
 * from the character the answer most resembles comes first under free matching
 * 0.0% of the time, and the learner's confusions sit at second or third. So
 * the middle rating stays a statement about order rather than a wider net.
 */
function isFirstOutOfOrder(properties: {
  expected: string;
  shape: CharacterShape;
  index: HandwritingIndex;
  entry: HandwritingEntry | undefined;
}): boolean {
  const { expected, shape, index, entry } = properties;

  if (entry === undefined) {
    return false;
  }

  const mine = orderFreeCost(shape, entry.shape);

  return !index.some(
    (one) =>
      one.character !== expected &&
      one.shape.length === shape.length &&
      orderFreeCost(shape, one.shape) < mine,
  );
}

/** The rating a reading earns. */
function ratingOf(verdict: HandwritingReading["verdict"]): Rating {
  if (verdict === "written") {
    return ratings.good;
  }

  return verdict === "outOfOrder" ? ratings.hard : ratings.again;
}

/**
 * The one mark a handwriting answer carries.
 *
 * A card asks for one character, so there is one piece, and the page draws it
 * the way it draws a marked character anywhere else. What the reader produced
 * is the character the site read out of their ink, which is not a character at
 * all where nothing was drawn.
 */
function markOf(
  expected: string,
  reading: HandwritingReading,
): GradedAnswer["pieces"][number] {
  if (reading.verdict === "written" || reading.verdict === "outOfOrder") {
    return { mark: "correct", expected, typed: expected };
  }

  return reading.looksLike === undefined
    ? { mark: "missing", expected }
    : { mark: "wrong", expected, typed: reading.looksLike };
}
